import axios from 'axios';

import { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types';

// Redux.
import { connect } from 'react-redux';
import {
    addSpeakerSubscribeIds,
    forMeLoadingCountDecrease,
    forMeLoadingCountIncrease,
    invalidateLatestSubscribed,
    setGetStartedTopicsStatusTrue,
    updateForMe
} from '../redux/actions/userActions';
import { setNextPreviewsInfo } from '../redux/actions/dataActions';

// Router.
import { useNavigate } from 'react-router-dom';

// Firebase.
import { getAnalytics, logEvent } from "firebase/analytics";

// Components.
import PodcastFlipper from '../components/PodcastFlipper';
import OnboardingLoading from '../components/OnboardingLoading';
import { validTokenExists } from '../firebaseHelper';
import { GET_STARTED_DATA, GET_STARTED_NEW_USER_RESPONSE } from '../constants/local_storage_constants';
import { startOnboardingSession } from '../utils/onboardingSession';
import { trackOnboardingError, trackOnboardingEvent } from '../utils/onboardingFunnel';
import { persistGetStartedDraft } from '../utils/getStartedSelections';


const parseGetStartedData = () => {
    const dataString = localStorage.getItem(GET_STARTED_DATA);
    if (!dataString) {
        return null;
    }
    // Make sure selectedTopics, unselectedTopics, selectedSpeakerIds, unselectedSpeakerIds exist.
    try {
        const data = JSON.parse(dataString);
        if (!data.selectedTopics || !data.unselectedTopics || !data.selectedSpeakerIds || !data.unselectedSpeakerIds) {
            return null;
        }
        return data;
    } catch (e) {
        localStorage.removeItem(GET_STARTED_DATA);
        return null;
    }
}


export const CuratePodcastsForYou = ({
    authenticated,
    addSpeakerSubscribeIds,
    forMeLoadingCountDecrease,
    forMeLoadingCountIncrease,
    invalidateLatestSubscribed,
    setGetStartedTopicsStatusTrue,
    setNextPreviewsInfo,
    updateForMe,
}) => {

    const navigate = useNavigate();
    const [curationAttempt, setCurationAttempt] = useState(0);
    const [curationError, setCurationError] = useState(false);
    const [guestCuration, setGuestCuration] = useState(authenticated !== true);
    const curationCompletedRef = useRef(false);

    useEffect(() => {
        logEvent(getAnalytics(), 'screen_view', {
            firebase_screen: 'curate_podcasts_for_you'
        });
    }, []);

    useEffect(() => {
        if (curationCompletedRef.current) {
            return;
        }

        let cancelled = false;
        const getStartedData = parseGetStartedData();
        if (!getStartedData) {
            navigate('/');
            return;
        }
        // Portrait metadata stays local; the server receives the existing picks payload.
        const { selectedSpeakers, ...selectionPayload } = getStartedData;
        setCurationError(false);

        const showCurationError = () => {
            if (!cancelled) {
                setCurationError(true);
            }
        };

        const handleCurationFailure = (error, memberCuration = false) => {
            if (cancelled) return;
            if (!memberCuration) trackOnboardingError('generate', error);
            const errorCode = error?.response?.data?.error || error?.error;
            const highlightsUnavailable = errorCode === 'starter_highlights_unavailable';
            if (highlightsUnavailable || ['starter_roster_stale', 'roster_stale'].includes(errorCode)) {
                curationCompletedRef.current = true;
                persistGetStartedDraft({
                    rosterVersion: getStartedData.rosterVersion,
                    selectedPairs: getStartedData.selectedPairs || [],
                });
                localStorage.removeItem(GET_STARTED_DATA);
                navigate(memberCuration ? '/settings/speakers' : '/#explore', {
                    replace: true,
                    state: highlightsUnavailable && !memberCuration ? { highlightsUnavailable: true } : null,
                });
            } else {
                showCurationError();
            }
        };
        
        validTokenExists()
            .then(() => {
                if (cancelled) return;
                setGuestCuration(false);
                forMeLoadingCountIncrease();

                axios
                    .post('/save_get_started_previews', selectionPayload)
                    .then((res) => {
                        forMeLoadingCountDecrease();
                        if (cancelled) {
                            return;
                        }
                        if (res.data.error) {
                            console.log(res.data.error);
                            handleCurationFailure(res.data, true);
                        } else {
                            curationCompletedRef.current = true;
                            localStorage.removeItem(GET_STARTED_DATA);
                            updateForMe(res.data);
                            addSpeakerSubscribeIds(getStartedData.selectedSpeakerIds);
                            invalidateLatestSubscribed();
                            setGetStartedTopicsStatusTrue();
                            navigate('/#explore');
                        }
                    })
                    .catch((err) => {
                        forMeLoadingCountDecrease();
                        console.log(err);
                        handleCurationFailure(err, true);
                    });
            }, () => {
                // Logged out.
                if (cancelled) return;
                setGuestCuration(true);
                trackOnboardingEvent('onboarding_generate_start');
                axios
                    .post('/save_get_started_new_user', selectionPayload)
                    .then((res) => {
                        if (cancelled) {
                            return;
                        }
                        if (res.data.error) {
                            console.log(res.data.error);
                            handleCurationFailure(res.data);
                        } else {
                            const onboardingData = {
                                ...res.data,
                                selectedSpeakerIds: getStartedData.selectedSpeakerIds,
                                selectedSpeakers,
                            };
                            const session = startOnboardingSession(onboardingData);
                            if (!session) {
                                trackOnboardingError('generate', { code: 'empty_sample' });
                                showCurationError();
                                return;
                            }
                            trackOnboardingEvent('onboarding_sample_ready');
                            localStorage.setItem(GET_STARTED_NEW_USER_RESPONSE, JSON.stringify(onboardingData));
                            setNextPreviewsInfo(false, [session.sample], {
                                initialPreviewId: session.sample.id,
                            });
                            curationCompletedRef.current = true;
                            localStorage.removeItem(GET_STARTED_DATA);
                            navigate('/previews', {
                                replace: true,
                                state: { onboarding: true },
                            });
                        }
                    })
                    .catch((err) => {
                        handleCurationFailure(err);
                    });
            });

        return () => {
            cancelled = true;
        };
    }, [
        addSpeakerSubscribeIds,
        curationAttempt,
        forMeLoadingCountDecrease,
        forMeLoadingCountIncrease,
        invalidateLatestSubscribed,
        navigate,
        setGetStartedTopicsStatusTrue,
        setNextPreviewsInfo,
        updateForMe,
    ]);

    return (
        <div className={guestCuration ? undefined : 'container bottomPadding'}>
            {guestCuration ?
                <OnboardingLoading
                    error={curationError
                        ? 'We couldn’t load your highlights. Your picks are safe.'
                        : null}
                    onRetry={() => setCurationAttempt((attempt) => attempt + 1)}
                />
                : curationError ?
                <div style={{ padding: '80px 20px 20px', textAlign: 'center' }}>
                    <div role='alert' style={{ marginBottom: '16px' }}>
                        We couldn’t finish your feed. Your picks are safe.
                    </div>
                    <button
                        type='button'
                        className='authSigninPill'
                        style={{ marginTop: 0 }}
                        onClick={() => setCurationAttempt((attempt) => attempt + 1)}
                    >
                        Try again
                    </button>
                </div>
                :
                <PodcastFlipper />
            }
        </div>
    );
}

CuratePodcastsForYou.propTypes = {
    authenticated: PropTypes.bool.isRequired
}

const mapStateToProps = (state) => ({
    authenticated: state.user.authenticated,
})

const mapActionsToProps = {
    addSpeakerSubscribeIds,
    forMeLoadingCountDecrease,
    forMeLoadingCountIncrease,
    invalidateLatestSubscribed,
    setGetStartedTopicsStatusTrue,
    setNextPreviewsInfo,
    updateForMe,
}

export default connect(mapStateToProps, mapActionsToProps)(CuratePodcastsForYou);
